import React, { useEffect, useRef, useState } from 'react'
import { ActivityIndicator, TextStyle, View, ViewStyle } from 'react-native'
import { observer } from 'mobx-react-lite'
import { useHistory, useLocation } from 'react-router-dom'

import { useStores, setPageTitle } from '@trivie/core'
import { Header, TopicCard, color, iconArrowBack } from '@trivie/ui-mobile'
import { Text } from '@trivie/ui-mobile/components/text'
import { Button } from '@trivie/ui-mobile/components/button'
import { useConfirm } from '@trivie/ui-web/components/dialogs/confirmation-dialog'

const useQuery = () => {
  const { search } = useLocation()
  return React.useMemo(() => new URLSearchParams(search), [search])
}

export const SubscriptionDetailScreen: React.FunctionComponent = observer(() => {
  const history = useHistory()
  const query = useQuery()
  const subscriptionId = query.get('subscription_id')
  const confirm = useConfirm()

  const { rootCache, userStore, libraryStore, subscriptionStore } = useStores()
  const { user } = userStore
  const { getContentPackDetails } = libraryStore
  const { ahaWebhook } = subscriptionStore

  const [loading, setLoading] = useState(true)
  const [packDetails, setPackDetails] = useState<any>(null)
  const hasLoaded = useRef(false)

  const load = async (hideLoader?: boolean) => {
    if (user && subscriptionId) {
      if (!hideLoader) {
        setLoading(true)
      }
      const success = await getContentPackDetails(user.id, subscriptionId)
      if (success) {
        hasLoaded.current = true
        setPackDetails(success)
        setLoading(false)
      } else {
        setLoading(false)
        history.goBack()
        confirm({
          title: 'Error',
          confirmationText: 'Okay',
          hideCancelButton: true,
          dialogText: 'There was an issue while trying to load this content pack',
        })
          .then(() => {})
          .catch(() => {})
      }
    } else {
      setLoading(false)
    }
  }

  useEffect(() => {
    setPageTitle(`Subscription - ${process.env.REACT_APP_SITE_TITLE}`)
    if (!hasLoaded.current) {
      load()
    }
  }, [user?.id, subscriptionId])

  const subscribe = async () => {
    const success = await ahaWebhook({
      app_id: 'com.trivie.engage.aha',
      content_pack_id: subscriptionId,
      enable: !packDetails.is_subscribed,
    })

    rootCache.invalidateAction('getTopicsForUser', libraryStore)
    rootCache.invalidateAction('getResourcesForUser', libraryStore)
    rootCache.invalidateAction('getQuizzesForUser', libraryStore)

    await load(true)
    if (success) {
      confirm({
        title: 'Success',
        confirmationText: 'Ok',
        hideCancelButton: true,
        dialogText: `You are now ${packDetails.is_subscribed ? 'unsubscribed from' : 'subscribed to'} the "${packDetails.title}" content pack`,
      })
        .then(() => {})
        .catch(() => {})
    } else {
      confirm({
        title: 'Error',
        danger: true,
        confirmationText: 'Ok',
        hideCancelButton: true,
        dialogText: `There was an issue trying to ${packDetails.is_subscribed ? 'unsubscribe from' : 'subscribe to'} the "${packDetails.title}" content pack`,
      })
        .then(() => {})
        .catch(() => {})
    }
  }

  const goToTopic = (topic: any) => {
    history.push(
      `/library/topic/?assignment_id=${topic.metadata?.assignment_id}&topic_id=${
        topic.topic?.topic_id || topic.metadata?.topic_id
      }&path_id=${topic.metadata?.path_id}&content_user_id=${topic.metadata?.content_user_id}`,
    )
  }

  return (
    <div style={CONTAINER}>
      <div style={HEADER_STICKY_CONTAINER}>
        <div style={HEADER_FLEX_CONTAINER}>
          <div style={HEADER}>
            <Header leftIcon={iconArrowBack} onLeftPress={history.goBack} center />
          </div>
        </div>
      </div>
      {loading || !packDetails ? (
        <ActivityIndicator
          color="#999"
          style={{ flex: 1, height: window.innerHeight - 100 }}
          size="small"
        />
      ) : (
        <div style={CONTENT}>
          <View>
            <Text style={{ marginBottom: 8 }} variant="headline" text={packDetails.title} />
            {packDetails.description ? (
              <Text
                variant="body1"
                text={packDetails.description}
                style={{ color: color.shade70, marginBottom: 16 }}
              />
            ) : null}
            <View style={{ marginBottom: 24 }}>
              <Button
                onPress={subscribe}
                variant={packDetails.is_subscribed ? 'outlined' : 'contained'}
                style={{ ...SUBSCRIBE_BTN, borderColor: color.blue }}
              >
                <View style={SUBSCRIBE_BTN_CONTENT}>
                  <Text
                    variant="titleEmphasized"
                    style={{
                      ...SUBSCRIBE_BTN_TEXT,
                      color: packDetails.is_subscribed ? color.blue : color.white,
                    }}
                    tx={packDetails.is_subscribed ? 'Unsubscribe' : 'Common.subscribe'}
                  />
                </View>
              </Button>
            </View>
          </View>
          <div style={GRID}>
            {packDetails.topics?.map((item: any) => {
              return (
                <TopicCard
                  key={`pack-topic-${item.topic_id}`}
                  locked={!packDetails.is_subscribed}
                  topic={{
                    ...item,
                    topic: {
                      quizzes_completed: Math.round(0.01 * item.completion_pct * item.quiz_count),
                      quiz_count: item.quiz_count,
                      pct_complete: item.completion_pct,
                      title: item.title,
                      photo: item.photo,
                      pack_color: packDetails.color,
                    },
                  }}
                  onPress={() => {
                    if (item.metadata) {
                      goToTopic({
                        ...item,
                        topic: {
                          quiz_count: item.quiz_count,
                          pct_complete: item.completion_pct,
                          topic_id: item.topic_id,
                        },
                        locked_by_pre_test: false,
                      })
                    }
                  }}
                />
              )
            })}
          </div>
        </div>
      )}
    </div>
  )
})

/**
 * Styling
 */
const CONTAINER: React.CSSProperties = {
  backgroundColor: color.white,
  height: '100%',
}
const CONTENT: React.CSSProperties = {
  flexDirection: 'column',
  display: 'flex',
  paddingBottom: 80,
  marginTop: 16,
}
const HEADER_STICKY_CONTAINER: React.CSSProperties = {
  position: 'sticky',
  top: 0,
  width: '100%',
  zIndex: 99,
  backgroundColor: 'white',
}
const HEADER_FLEX_CONTAINER: React.CSSProperties = {
  display: 'flex',
  maxWidth: '1440px',
  justifyContent: 'space-between',
}
const HEADER: React.CSSProperties = { width: '100%' }
const SUBSCRIBE_BTN: ViewStyle = {
  width: '100%',
  height: 48,
  borderRadius: 100,
}
const SUBSCRIBE_BTN_CONTENT: ViewStyle = { flexDirection: 'row', justifyContent: 'center' }
const SUBSCRIBE_BTN_TEXT: TextStyle = { color: color.white }
const GRID: React.CSSProperties = {
  display: 'grid',
  gridTemplateColumns: 'repeat(auto-fit, minmax(186px, 1fr))',
  gridGap: 16,
  gridAutoRows: 'minmax(80, auto)',
}
